export const ACCOUNT_PLAN_IDS = ["monthly", "quarterly", "annual"] as const;

export type AccountPlanId = (typeof ACCOUNT_PLAN_IDS)[number];

export type AccountPlan = {
  id: AccountPlanId;
  label: string;
  unitAmount: number;
  currency: string;
  interval: string;
  intervalCount: number;
  recommended: boolean;
  trialDays: number;
};

export type AccountMembership = {
  kind: "active" | "trial" | "complimentary" | "inactive";
  planId: AccountPlanId | null;
  renewsOrEndsAtUtc: string | null;
  nativeBillingStore: "app_store" | "play_store" | "unknown" | null;
};

export type AccountStatus = {
  access: "active" | "inactive" | "checking" | "unknown";
  email: string;
  canManagePlan: boolean;
  canChoosePlan: boolean;
  membership: AccountMembership;
  plans: AccountPlan[];
};

export type AccountPreviewMode =
  | "sign-in"
  | "loading"
  | "active"
  | "no-access"
  | "checking"
  | "error";

const planLabels: Record<AccountPlanId, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual"
};

export const isAccountPlanId = (value: unknown): value is AccountPlanId =>
  typeof value === "string" && ACCOUNT_PLAN_IDS.includes(value as AccountPlanId);

export const normalizeAccountPlan = (value: unknown): AccountPlan | null => {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;
  if (!isAccountPlanId(source.id)) return null;

  const unitAmount = typeof source.unitAmount === "number" && Number.isFinite(source.unitAmount)
    ? source.unitAmount
    : null;
  const intervalCount = typeof source.intervalCount === "number" && source.intervalCount > 0
    ? Math.floor(source.intervalCount)
    : 1;
  const currency = typeof source.currency === "string" ? source.currency.trim().toUpperCase() : "";
  const interval = typeof source.interval === "string" ? source.interval.trim().toLowerCase() : "";
  const trialDays = typeof source.trialDays === "number" && source.trialDays > 0
    ? Math.floor(source.trialDays)
    : 0;

  if (unitAmount === null || unitAmount <= 0 || !currency || !interval) return null;

  return {
    id: source.id,
    label: planLabels[source.id],
    unitAmount,
    currency,
    interval,
    intervalCount,
    recommended: source.id === "annual",
    trialDays
  };
};

const normalizeMembership = (
  value: unknown,
  access: AccountStatus["access"]
): AccountMembership => {
  const source = value && typeof value === "object"
    ? value as Record<string, unknown>
    : {};
  const kind = source.kind === "trial" || source.kind === "complimentary" || source.kind === "active"
    ? source.kind
    : access === "active"
      ? "active"
      : "inactive";
  const renewsOrEndsAtUtc = typeof source.renewsOrEndsAtUtc === "string" &&
    Number.isFinite(Date.parse(source.renewsOrEndsAtUtc))
    ? source.renewsOrEndsAtUtc
    : null;

  return {
    kind,
    planId: isAccountPlanId(source.planId) ? source.planId : null,
    renewsOrEndsAtUtc,
    nativeBillingStore: source.nativeBillingStore === "app_store" ||
      source.nativeBillingStore === "play_store" || source.nativeBillingStore === "unknown"
      ? source.nativeBillingStore : null
  };
};

export const normalizeAccountStatus = (value: unknown): AccountStatus | null => {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;
  const access = source.access;
  const email = typeof source.email === "string" ? source.email.trim().toLowerCase() : "";
  if (
    (access !== "active" && access !== "inactive" && access !== "checking" && access !== "unknown") ||
    !email
  ) {
    return null;
  }

  return {
    access,
    email,
    canManagePlan: source.canManagePlan === true,
    canChoosePlan: source.canChoosePlan === true,
    membership: normalizeMembership(source.membership, access),
    plans: Array.isArray(source.plans)
      ? source.plans.map(normalizeAccountPlan).filter((plan): plan is AccountPlan => plan !== null)
      : []
  };
};

export const formatAccountPlanPrice = (plan: AccountPlan): {
  total: string;
  cadence: string;
  equivalent: string | null;
} => {
  const total = formatAccountCurrency(plan.unitAmount, plan.currency);
  const intervalLabel = plan.intervalCount === 1
    ? plan.interval
    : `${plan.intervalCount} ${plan.interval}s`;
  const cadence = `${total} every ${intervalLabel}`;

  if (plan.interval !== "year" && !(plan.interval === "month" && plan.intervalCount > 1)) {
    return { total, cadence, equivalent: null };
  }

  const months = plan.interval === "year" ? plan.intervalCount * 12 : plan.intervalCount;
  return {
    total,
    cadence,
    equivalent: `${formatAccountCurrency(plan.unitAmount / months, plan.currency)} per month`
  };
};

export const formatAccountCurrency = (unitAmount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat("en", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 2
  });
  return `${formatter.format(unitAmount / 100)} ${currency}`;
};

const accountPlanMonths = (plan: AccountPlan): number | null => {
  if (plan.interval === "year") return plan.intervalCount * 12;
  if (plan.interval === "month") return plan.intervalCount;
  return null;
};

export const accountPlanReferenceAmount = (
  plan: AccountPlan,
  plans: AccountPlan[]
): number | null => {
  const monthly = plans.find((candidate) =>
    candidate.id === "monthly" &&
    candidate.interval === "month" &&
    candidate.intervalCount === 1 &&
    candidate.currency === plan.currency
  );
  const months = accountPlanMonths(plan);
  if (!monthly || monthly.unitAmount <= 0 || !months || plan.id === "monthly") return null;
  return monthly.unitAmount * months;
};

export const accountPlanSavingsPercent = (
  plan: AccountPlan,
  plans: AccountPlan[]
): number | null => {
  const referenceAmount = accountPlanReferenceAmount(plan, plans);
  if (!referenceAmount) return null;
  const percentage = Math.round((1 - plan.unitAmount / referenceAmount) * 100);
  return percentage > 0 ? percentage : null;
};

export const accountPreviewMode = (search: string, isDevelopment: boolean): AccountPreviewMode | null => {
  if (!isDevelopment) return null;
  const value = new URLSearchParams(search).get("preview");
  return value === "sign-in" || value === "loading" || value === "active" || value === "no-access" || value === "checking" || value === "error"
    ? value
    : null;
};

export const previewStatus = (mode: Exclude<AccountPreviewMode, "sign-in" | "loading" | "error">): AccountStatus => ({
  access: mode === "active" ? "active" : mode === "checking" ? "checking" : "inactive",
  email: "you@example.com",
  canManagePlan: mode === "active",
  canChoosePlan: mode === "no-access",
  membership: {
    kind: mode === "active" ? "trial" : "inactive",
    planId: mode === "active" ? "annual" : null,
    renewsOrEndsAtUtc: mode === "active" ? "2026-08-14T00:00:00.000Z" : null,
    nativeBillingStore: null
  },
  plans: mode === "no-access"
    ? [
        { id: "monthly", label: "Monthly", unitAmount: 1499, currency: "USD", interval: "month", intervalCount: 1, recommended: false, trialDays: 0 },
        { id: "quarterly", label: "Quarterly", unitAmount: 2999, currency: "USD", interval: "month", intervalCount: 3, recommended: false, trialDays: 0 },
        { id: "annual", label: "Annual", unitAmount: 4900, currency: "USD", interval: "year", intervalCount: 1, recommended: true, trialDays: 3 }
      ]
    : []
});

export const nativeMembershipManagement = (
  store: AccountMembership["nativeBillingStore"]
): { description: string; label: string | null; url: string | null } | null => {
  if (store === "app_store") return {
    description: "Your Sehaj membership is billed through the App Store. Use the Apple Account you subscribed with to manage it.",
    label: "Manage App Store subscription",
    url: "https://account.apple.com/account/manage/section/subscriptions"
  };
  if (store === "play_store") return {
    description: "Your Sehaj membership is billed through Google Play. Use the Google Account you subscribed with to manage it.",
    label: "Manage Google Play subscription",
    url: "https://play.google.com/store/account/subscriptions"
  };
  if (store === "unknown") return {
    description: "Manage your membership in the store where you subscribed. Contact support if you cannot find it.",
    label: null,
    url: null
  };
  return null;
};

export const escapeAccountHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;"
  })[character] ?? character);
